import { CartAddEvent } from '@theme/events';

/**
 * Chief Locks - « Compléter la commande » (tiroir panier).
 * Délégation d'événements sur document : continue de fonctionner quand le tiroir est rafraîchi par le thème.
 * Mobile : glisser au doigt. Ordinateur : flèches, glisser à la souris, points.
 */
(function () {
  if (window.__clCartComplete) return;
  window.__clCartComplete = true;

  const ADD_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
  const FEEDBACK_MS = 650;
  const ERROR_MS = 3500;

  /** @param {Element} track */
  const cardWidth = (track) => {
    const first = track.querySelector('[data-cl-cc-card]');
    if (!first) return track.clientWidth || 1;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  };

  /** @param {Element} track */
  const syncUi = (track) => {
    const root = track.closest('[data-cl-cc]');
    if (!root) return;
    const width = cardWidth(track);
    const index = Math.round(track.scrollLeft / width);
    const dots = root.querySelectorAll('[data-cl-cc-dot]');
    dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

    const prev = root.querySelector('[data-cl-cc-prev]');
    const next = root.querySelector('[data-cl-cc-next]');
    const max = track.scrollWidth - track.clientWidth - 2;
    if (prev instanceof HTMLButtonElement) prev.disabled = track.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = track.scrollLeft >= max;
  };

  let ticking = false;
  document.addEventListener(
    'scroll',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.matches('[data-cl-cc-track]')) return;
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        syncUi(target);
      });
    },
    true
  );

  /* Ordinateur : glisser à la souris (le tactile reste géré nativement par le navigateur). */
  let drag = null;
  let justDragged = false;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const track = target.closest('[data-cl-cc-track]');
    if (!(track instanceof HTMLElement)) return;
    if (target.closest('button, select, a, input')) return;
    drag = { track, startX: event.clientX, startLeft: track.scrollLeft, moved: false, id: event.pointerId };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(dx) > 5) {
      drag.moved = true;
      drag.track.classList.add('is-dragging');
      try {
        drag.track.setPointerCapture(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }
    if (drag.moved) drag.track.scrollLeft = drag.startLeft - dx;
  });

  const endDrag = (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const { track, moved } = drag;
    drag = null;
    if (!moved) return;
    justDragged = true;
    setTimeout(() => {
      justDragged = false;
    }, 0);
    track.classList.remove('is-dragging');
    const width = cardWidth(track);
    track.scrollTo({ left: Math.round(track.scrollLeft / width) * width, behavior: 'smooth' });
  };
  document.addEventListener('pointerup', endDrag);
  document.addEventListener('pointercancel', endDrag);

  /** @param {HTMLElement} root @param {string} message */
  const announce = (root, message) => {
    const live = root.querySelector('[data-cl-cc-live]');
    if (live) live.textContent = message;
  };

  /** @param {HTMLElement} card @param {string} message */
  const showError = (card, message) => {
    const box = card.querySelector('[data-cl-cc-error]');
    if (!box) return;
    box.textContent = message;
    box.removeAttribute('hidden');
    setTimeout(() => box.setAttribute('hidden', ''), ERROR_MS);
  };

  const sectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  };

  /** @param {HTMLButtonElement} button */
  async function addToCart(button) {
    const card = button.closest('[data-cl-cc-card]');
    const root = button.closest('[data-cl-cc]');
    if (!(card instanceof HTMLElement) || !(root instanceof HTMLElement)) return;
    if (button.disabled) return;

    const select = card.querySelector('[data-cl-cc-variant]');
    const variantId = select instanceof HTMLSelectElement ? select.value : button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');
    const ids = sectionIds();
    if (ids.length) {
      body.append('sections', ids.join(','));
      body.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(ADD_URL, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Impossible d’ajouter ce produit.');
      }

      let itemCount;
      let cart;
      try {
        cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        itemCount = cart.item_count;
      } catch (_) {
        /* le décompte sera rafraîchi avec la section */
      }

      button.classList.remove('is-loading');
      button.classList.add('is-done');
      announce(root, 'Produit ajouté au panier');

      setTimeout(() => {
        card.dispatchEvent(
          new CartAddEvent(cart, 'cl-cart-complete', {
            source: 'cl-cart-complete',
            itemCount,
            variantId,
            productId: card.dataset.productId,
            sections: data.sections,
          })
        );
      }, FEEDBACK_MS);
    } catch (error) {
      button.classList.remove('is-loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
      showError(card, error instanceof Error ? error.message : 'Une erreur est survenue.');
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (justDragged && target.closest('[data-cl-cc-track]')) {
      event.preventDefault();
      return;
    }

    const addButton = target.closest('[data-cl-cc-add]');
    if (addButton instanceof HTMLButtonElement) {
      event.preventDefault();
      addToCart(addButton);
      return;
    }

    const arrow = target.closest('[data-cl-cc-prev], [data-cl-cc-next]');
    if (arrow instanceof HTMLElement) {
      const root = arrow.closest('[data-cl-cc]');
      const track = root && root.querySelector('[data-cl-cc-track]');
      if (track) {
        const dir = arrow.hasAttribute('data-cl-cc-next') ? 1 : -1;
        const width = cardWidth(track);
        const index = Math.round(track.scrollLeft / width) + dir;
        track.scrollTo({ left: Math.max(0, index) * width, behavior: 'smooth' });
      }
      return;
    }

    const dot = target.closest('[data-cl-cc-dot]');
    if (dot instanceof HTMLElement) {
      const root = dot.closest('[data-cl-cc]');
      const track = root && root.querySelector('[data-cl-cc-track]');
      if (track) track.scrollTo({ left: Number(dot.dataset.clCcDot) * cardWidth(track), behavior: 'smooth' });
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-cl-cc-variant]')) return;
    const card = select.closest('[data-cl-cc-card]');
    if (!card) return;
    const option = select.selectedOptions[0];
    const price = card.querySelector('[data-cl-cc-price]');
    if (option && price && option.dataset.price) price.textContent = option.dataset.price;
    const button = card.querySelector('[data-cl-cc-add]');
    if (button instanceof HTMLElement) button.dataset.variantId = select.value;
  });
})();
